import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import {
	RENDER_INCREMENT
} from './datasets-tabular-result'
import calendars from 'scoreboard/components/calendars'
import BootslideMenu from 'scoreboard/components/bootslide/bootslide-menu'
import ArgumentTruncator from 'scoreboard/components/argument-truncator'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import nestModel from 'scoreboard/components/nest-model'
import Backbone from 'backbone'
import getChooseSummaryTypeDefault from './get-choose-summary-type-default'
import unwrapModel from 'scoreboard/components/unwrap-model'
import aggregationLabel from './aggregation-label'
import GroupSimilarTimeRangesView from './group-similar-time-ranges-view'
import { TABLE_FIELD, BREAKDOWN_ROW, ROLLUP_FIELD_ANCESTORS, ROLLUP_FIELD_KEYS } from 'scoreboard/datasets/overview/datasets-tabular-result'
import { getRepeatByGroupingKey } from './get-repeat-by-grouping'
import wrapModel from 'scoreboard/components/wrap-model'
import cloneWithoutResponseDataset from '../clone-without-response-dataset'
import isGeographicOrRollup from '../is-geographic-or-rollup'

import SummaryType from 'scoreboard/datasets/model/summary-type'
const {
    DAY_OF_THE_WEEK,
    MONTH_OF_THE_YEAR,
    SIMILAR_TIME_RANGES,
    HOUR_OF_THE_DAY,
    MINIMUM,
    MAXIMUM,
    UNIQUE,
    MIN_DATE,
    MAX_DATE,
    MIN_TIME,
    MAX_TIME,
    SUM,
    AVERAGE,
    VALUE,
    RANGE,
	LIST_UNIQUE_VALUES
} = SummaryType

import constants from 'scoreboard/components/constants'
const { LINKED } = constants
import DataType from 'scoreboard/datasets/model/data-type'
const {
	TEXT, YES_NO, DATE, TIME,
	DATE_TIME, CALENDAR_PERIOD, ROLLUP, DATASET_LINK
} = DataType
import Dataset from 'scoreboard/datasets/model/dataset'
const { isTotal } = Dataset

/**
 * Returns whether the result for a table would only be a numeric aggregation
 * like sum or average.
 * @param {Object} underlyingField the UNDERLYING field for a dataset. Used to
 * look at the data type of the data to display in the table.
 */
function aggregatedResult(underlyingField, table) {
	underlyingField = unwrapModel(underlyingField)
	// #51740
	if (underlyingField?.type === LINKED) {
		throw Error('Need to pass in the underlying field instead (use Dataset.getUnderlyingField)')
	}

	table = unwrapModel(table)
	// #15569 can count unique values for every field type
	if ([UNIQUE, LIST_UNIQUE_VALUES].includes(table?.summaryType)) {
		return true
	}

	if (isTotal(table?.fieldId)) {
		return true
	}

	if (underlyingField?.dataType &&
		!isNumericDataType(underlyingField?.dataType) &&
		!_.includes([DATE, TIME, DATE_TIME], underlyingField?.dataType)) {
		return false
	}

	if (_.includes([
		SUM, AVERAGE, MINIMUM, MAXIMUM, MIN_DATE, MAX_DATE, MIN_TIME, MAX_TIME
	], table?.summaryType)) {
		return true
	}
	if (table?.summaryType == VALUE || table?.summaryType == RANGE) {
		return false
	}

	// if we reach here table.summaryType was unspecified. In that case, match
	// the server's behavior from DatasetTableImpl:isSingleResult (#58697)
	return false
}

/**
 * Settings for tables on the datasets overview.  When an option is chosen in
 * the menu, an event fires.  Here are the events:
 *
 * <ul>
 *     <li>showing - argument: 'sum', 'average', or 'breakdown'</li>
 *     <li>manual-filter - indicates a manual f should be applied</li>
 *     <li>num-results - argument: 5, 10, etc.  Changes the number of results displayed</li>
 *     <li>breakdown-calendar - argument: a calendar ID</li>
 *     <li>app-calendar - argument: a field ID</li>
 * </ul>
 */
var TableSettings = BootslideMenu.extend({

	/**
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {SettingsModel} options.model a special model of the settings
	 *
	 * @param {Dataset} options.model.get('dataset') the Dataset model
	 *
	 * @param {Object} options.model.field the field this table is based on
	 * (saved to this.options.field)
	 *
	 * @param {DatasetTable} options.model.table the settings used for the table
	 * (saved to table)
	 */
	initialize: function (options) {
		this.options = options.model.toJSON()
		BootslideMenu.prototype.initialize.apply(this, arguments)
		this.childViews = []
	},

	menu: function() {
		var targets
		let model = unwrapModel(this.model)
		var table = this.model.table
		let field = model.field
		let underlyingField = model.dataset.getUnderlyingField(field)

		var menu = {
			label: i18n.get('common.options'),
			target: [ ],
			width: 250
		}

		// Choose Rows
		this.chooseRows(menu)

		// View Data Source
		menu.target.push({
			label: i18n.get('datasets.choose.viewDataSource'),
			target: this.eventTarget('view-data-source')
		})

		// Choose Columns, Set breakdown rows, Set breakdown columns
		const noRows = ['sum', 'average', 'minimum', 'maximum', 'unique' ]
		if (!_.includes(noRows, unwrapModel(table).summaryType)) {
			menu.target.push({
				label: i18n.get('datasets.choose.columns'),
				target: this.eventTarget('choose-columns')
			})
			menu.target.push({
				label: i18n.get('datasets.breakdown.columns'),
				target: this.eventTarget('set-repeat-by-grouping-columns')
			})
			menu.target.push({
				label: i18n.get('datasets.breakdown.rows'),
				target: this.eventTarget('set-repeat-by-grouping-rows')
			})
		}

		// Set Manual Filter
		/* #14912 set manual filter doesn't make sense anymore on datasets overview
		because more than one filter can apply per dataset table.  On Dashboards we
		should keep around the "Set Filters" option, though.
		menu.target.push({
			label: _.capitalize(i18n.get('datasets.filters.set')),
			target: this.eventTarget('manual-filter')
		})
		*/

		// Sort Column, Sort Order
		if (this.options.model.get('showSort')) {
			this.sortField(menu)
		}

		if (isGeographicOrRollup(underlyingField?.dataType)) {
			// Show Keys
			menu.target.push({
				label: i18n.get('datasets.field.showKeys.short'),
				contentView: BootslideMenu.ToggleProperty.extend({
					property: {
						name: 'showKeys',
						label: i18n.get('datasets.field.showKeys.short')
					}
				})
			})

			// Show ancestors
			menu.target.push({
				label: i18n.get('datasets.field.showAncestors'),
				contentView: BootslideMenu.ToggleProperty.extend({
					property: {
						name: 'showAncestors',
						label: i18n.get('datasets.field.showAncestors')
					}
				})
			})

		}

		// Abbreviate Numbers
		menu.target.push({
			label: i18n.get('common.abbreviateNumbers'),
			contentView: BootslideMenu.ToggleProperty.extend({
				property: {
					name: 'abbreviateNumbers',
					label: i18n.get('common.abbreviateNumbers')
				}
			})
		})

		// Showing X Rows
		var rowBreakdown = !!_.get(unwrapModel(table).rowBreakdown, 'fieldId')
		var columnBreakdown = !!_.get(unwrapModel(table).columnBreakdown, 'fieldId')
		// make sure a default value is set
		if (!table.get('numResults')) {
			table.set('numResults', 5, { silent: true })
		}
		if (!aggregatedResult(field, table) &&
				!this.options.showAll && !rowBreakdown && !columnBreakdown) {
			var numResultsText = i18n.get('datasets.showingRows', table.get('numResults') ||
					RENDER_INCREMENT)

			targets = _.map([5, 10, 15, 25, 50], function(value) {
				return {
					label: '' + value, // Bootslide doesn't like numbers as labels
					contentView: BootslideMenu.MultipleChoiceOption.extend({
						label: '' + value,
						value: value,
						property: 'numResults'
					}),
					model: table
				}
			}.bind(this))

			menu.target.push({
				label: numResultsText,
				contentView: BootslideMenu.MultipleChoiceMenu.extend({
					property: 'numResults',
					label: numResultsText
				}),
				model: table,
				target: targets
			})
		}

		return menu
	},

	/**
	 * Adds the "Choose Rows" option to the menu
	 */
	chooseRows: function(menu) {
		// #55807 for "Number of " tables, there should be no "Choose Rows" option
		if (!unwrapModel(this.model).field) return

		let options = []
		let model = unwrapModel(this.model)
		let underlyingField = model.dataset.getUnderlyingField(model.field)
		let fieldLabel = _.escape(underlyingField?.label)
		const dataType = underlyingField?.dataType
		if (isNumericDataType(underlyingField?.dataType) ||
			!_.isEmpty(underlyingField?.ranges)) {
			var rangeKeySuffix = isNumericDataType(underlyingField?.dataType) ?
				'numeric' : 'text'
			var rangeKey = 'datasets.choose.rows.range.' + rangeKeySuffix

			options.push({
				key: 'datasets.choose.rows.values',
				substitution: fieldLabel,
				value: 'value'
			})
			if (!_.isEmpty(underlyingField?.ranges)) {
				options.push({
					key: rangeKey,
					substitution: fieldLabel,
					value: 'range'
				})
			}
			if (isNumericDataType(underlyingField?.dataType)) {
				options.push({
					key: i18n.get('datasets.choose.rows.sum'),
					value: 'sum'
				}, {
					key: i18n.get('datasets.choose.rows.avg'),
					value: 'average'
				}, {
					key: i18n.get('datasets.minimum'),
					value: 'minimum'
				}, {
					key: i18n.get('datasets.maximum'),
					value: 'maximum'
				})
			}
		}
		if (_.includes([DATE, DATE_TIME, CALENDAR_PERIOD], underlyingField?.dataType)) {
			let self = this
			options.push(..._.map(calendars.calendars, (calendar) => {
				return {
					optionProvider: {
						label: i18n.get(calendar.label),
						contentView: BootslideMenu.MultipleChoiceOption.extend({
							label: i18n.get(calendar.label),
							value: calendar.id,
							property: 'breakdownCalendarId',
							selected: function() {
								// #36028 Since breakdownCalendarId is used with
								// the `similar-time-ranges` option as well, we
								// need to make sure we aren't showing the checkmark
								// here unless we explicitly picked a calendar option
								return this.model.get(this.property) == this.value &&
									this.model.get('summaryType') !== 'similar-time-ranges'
							},
							updateModel: function() {
								this.model.set(this.property, this.value)
								self.trigger('change-breakdown-calendar-id')
							}
						}),
						model: this.model
					}
				}
			}))

			if (underlyingField?.dataType != CALENDAR_PERIOD &&
				// #50425 need at least 2 calendars to group by similar time ranges
				calendars.calendars.length > 1) {
				options.push({
					// #36028 for this specific option we need to allow the user to
					// change additional settings, so we use this `optionProvider` to
					// build a custom menu option
					optionProvider: {
						label: i18n.get('datasets.choose.groupSimilarTimeRanges'),
						contentView: BootslideMenu.MultipleChoiceOption.extend({
							label: i18n.get('datasets.choose.groupSimilarTimeRanges'),
							value: 'similar-time-ranges',
							property: 'summaryType',
							// #38378 we don't want to update the model or fire events
							// when this option is clicked, so we overwrite this function
							// to do nothing
							select: () => {
							}
						}),
						model: this.model,
						contentEndpoint: () => {
							const view = new GroupSimilarTimeRangesView({
								model: this.model
							})
							this.childViews.push(view)
							view.on('cancel', this.back.bind(this))
							view.on('done', data => {
								this.trigger('updateSimilarTimeRanges', data)
							})
							// When the time ranges change, resize the bootslide menu
							view.on('change', () => this.setBootslideWidth(view.el.clientWidth, true))
							return view.render().el
						}
					}
				})
			}
		}

		if (underlyingField?.dataType === DATE) {
			options.push({
				key: 'datasets.minimum.date',
				value: 'minimum',
				property: 'summaryType'
			}, {
				key: 'datasets.maximum.date',
				value: 'maximum',
				property: 'summaryType'
			})
		}

		if (underlyingField?.dataType === TIME) {
			options.push({
				key: i18n.get('scorecards.node.metric.dataType.standard'),
				value: 'standard',
				property: 'summaryType'
			}, {
				key: 'datasets.minimum.time',
				value: 'minimum',
				property: 'summaryType'
			}, {
				key: 'datasets.maximum.time',
				value: 'maximum',
				property: 'summaryType'
			})
		}

		if (underlyingField?.dataType === DATE_TIME) {
			options.push({
				key: 'datasets.minimum.date',
				value: 'min-date',
				property: 'summaryType'
			}, {
				key: 'datasets.minimum.time',
				value: 'min-time',
				property: 'summaryType'
			}, {
				key: 'datasets.minimum.dateTime',
				value: 'minimum',
				property: 'summaryType'
			}, {
				key: 'datasets.maximum.date',
				value: 'max-date',
				property: 'summaryType'
			}, {
				key: 'datasets.maximum.time',
				value: 'max-time',
				property: 'summaryType'
			}, {
				key: 'datasets.maximum.dateTime',
				value: 'maximum',
				property: 'summaryType'
			})
		}

		if (underlyingField?.dataType === TIME ||
				underlyingField?.dataType === DATE_TIME) {
			options.push({
				key: 'datasets.choose.hourOfTheDay',
				value: 'hour-of-the-day',
				property: 'summaryType'
			})
		}

		if (isGeographicOrRollup(underlyingField?.dataType)) {
			options.push({
				key: 'datasets.listFields',
				value: 'list',
				property: 'summaryType'
			}, {
				key: 'datasets.field.levels.1',
				value: 'tree',
				property: 'summaryType'
			}, {
				key: 'datasets.field.levels.2',
				value: 'grandchildren',
				property: 'summaryType'
			}, {
				key: 'datasets.field.levels.3',
				value: 'great-grandchildren',
				property: 'summaryType'
			})
		}


		if (_.isEmpty(options)) {
			options.push({
				key: 'datasets.choose.rows.values',
				substitution: fieldLabel,
				value: _.includes([YES_NO, TEXT], dataType) ?
					'standard' : 'value'
			})
		}

		// "Number of Unique" option available for all field types.  It's at the
		// end because it probably won't be used very often, and we want the most
		// important options at the beginning
		options.push({
			key: i18n.get('datasets.unique'),
			value: 'unique'
		})
		// "List unique values" also could be available for all field types.
		// However, it seems of limited utility, so we're not going to show it
		// for now
		// options.push({
		// 	key: i18n.get('common.listUniqueValues'),
		// 	value: LIST_UNIQUE_VALUES
		// })
		var target = _.map(options, option => {
			if (option.optionProvider) {
				// #36028 for the "group similar ranges" feature, we need to allow
				// the user to set up the grouping
				return option.optionProvider
			}
			return this.createMultiChoiceOption(option)
		})
		menu.target.push({
			label: i18n.get('datasets.choose.rows'),
			target: target,
			width: 250
		})
	},

	/**
	 * Adds the Sort Column submenu to the menu
	 */
	sortField: function(menu) {
		const property = 'sortField'
		const model = unwrapModel(this.model)
		/** @type {Dataset} */
		const dataset = model.dataset
		/** @type {DatasetTable} */
		const table = model.table

		// always include sort properties in the table settings
		if (!table.get('sortField')) {
			table.unset('sortField')
		}

		// All the options except for Default
		const baseSortFieldOption = {
			model: table
		}
		const baseMultipleChoiceOption = {
			property,
			updateModel
		}
		function updateModel(value) {
			// clear the sort order when selecting Default or a new sort field
			if (value === null ||  this.model.get(property) !== value) {
				this.model.unset('ascending')
			}
			BootslideMenu.MultipleChoiceOption.prototype.updateModel
				.apply(this, arguments)
		}

		const target = []

		// Add the Default sort option
		const defaultLabel = i18n.get('dashboards.datasets.sort.default')
		target.push({
			label: defaultLabel,
			id: 'dataset-sort-default',
			contentView: BootslideMenu.MultipleChoiceOption.extend(
				_.extend({}, baseMultipleChoiceOption, {
				label: defaultLabel,
				value: null,
				selected: function() { return !this.model.get(property) }
			})),
			model: table
		})

		/**
		 *  Adding keys and ancestors even if table.showKeys === false because they
		 *  are toggled off using bottslide.toggleItem
		 *  {@see dataset-table-widget-config.js#toggleSortMenuItems}
		 */
		// Add the keys column
		const keyLabel = _.escape(i18n.get('datasets.trees.keys.noArg'))
		target.push(_.extend({}, baseSortFieldOption, {
			target: this._sortOrderMenu({dataType: ROLLUP}),
			label: keyLabel,
			id: 'dataset-sort-keys',
			contentView: BootslideMenu.MultipleChoiceOption.extend(
				_.extend({}, baseMultipleChoiceOption, {
					label: keyLabel,
					value: ROLLUP_FIELD_KEYS
				}))
		}))

		// Add the ancestors column
		const ancestorsLabel = _.escape(i18n.get('datasets.field.showAncestors.noArg'))
		target.push(_.extend({}, baseSortFieldOption, {
			target: this._sortOrderMenu({dataType: ROLLUP}),
			label: ancestorsLabel,
			id: 'dataset-sort-ancestors',
			contentView: BootslideMenu.MultipleChoiceOption.extend(
				_.extend({}, baseMultipleChoiceOption, {
					label: ancestorsLabel,
					value: ROLLUP_FIELD_ANCESTORS
				}))
		}))

		// Add the main field column
		const fieldLabel = dataset.getFieldLabel(model.field.id)
		target.push(_.extend({}, baseSortFieldOption, {
			target: this._sortOrderMenu(model.field),
			label: fieldLabel,
			contentView: BootslideMenu.MultipleChoiceOption.extend(
				_.extend({}, baseMultipleChoiceOption, {
					label: fieldLabel,
					value: TABLE_FIELD
				}))
		}))

		// Add the breakdown row, if any
		const rowBreakdown = table.get(getRepeatByGroupingKey('row'))
		const showBreakdown = !_.isEmpty(rowBreakdown) && rowBreakdown.show
		if (showBreakdown) {
			const breakdownLabel = dataset.getFieldLabel(rowBreakdown.fieldId)
			target.push(_.extend({}, baseSortFieldOption, {
				target: this._sortOrderMenu(dataset.getFieldByIds(rowBreakdown)),
				label: breakdownLabel,
				contentView: BootslideMenu.MultipleChoiceOption.extend(
					_.extend({}, baseMultipleChoiceOption, {
						label: breakdownLabel,
						value: BREAKDOWN_ROW
					}))
			}))
		}

		// Can't sort on aggregation columns if there is a row breakdown
		if (!showBreakdown) {
			// Add aggregation columns
			const aggregations = table.get('aggregations')
			aggregations.forEach(aggregation => {
				const label = aggregationLabel(dataset, aggregation)
				let field = aggregation.dataSourceType === 'calculated'
					? { dataType: aggregation.dataType }
					: dataset.getFieldByIds(aggregation)

				target.push(_.extend({}, baseSortFieldOption, {
					target: this._sortOrderMenu(field),
					label,
					contentView: BootslideMenu.MultipleChoiceOption.extend(
						_.extend({}, baseMultipleChoiceOption, {
							label,
							value: aggregation.sortField
						}))
				}))
			})
		}

		const label = i18n.get('dashboards.datasets.sort')
		menu.target.push({
			label,
			contentView: BootslideMenu.MultipleChoiceMenu.extend({
				label,
				property
			}),
			model: this.model.table,
			target
		})
	},

	/**
	 * Returns a sort order menu to be used on all dataset fields except for the
	 * Default sort field option
	 */
	_sortOrderMenu: function(field) {
		if (field.dataType === DATASET_LINK) {
			field = this.model.get('dataset').getUnderlyingField(field)
		}
		/**
		 * {@see scoreboard/datasets/edit/data-types.js}
		 * 'number', 'percent', 'currency', and unknown dataTypes default
		 */
		let ascending = i18n.get('common.search.sort.number.asc')
		let descending = i18n.get('common.search.sort.number.desc')
		if (isGeographicOrRollup(field.dataType) || field.dataType === TEXT) {
			ascending = i18n.get('common.search.sort.alpha.asc')
			descending = i18n.get('common.search.sort.alpha.desc')
		} else if (field.dataType === DATE) {
			ascending = i18n.get('common.search.sort.date.asc')
			descending = i18n.get('common.search.sort.date.desc')
		} else if (field.dataType === YES_NO) {
			ascending = i18n.get('common.search.sort.yesNo.asc')
			descending = i18n.get('common.search.sort.yesNo.desc')
		}

		return [{
			label: ascending,
			contentView: BootslideMenu.MultipleChoiceOption.extend({
				label: ascending,
				property: 'ascending',
				// #40319 dates are backwards for some reason
				value: field.dataType !== DATE
			}),
			model: this.model.table
		}, {
			label: descending,
			contentView: BootslideMenu.MultipleChoiceOption.extend({
				label: descending,
				property: 'ascending',
				// #40319 dates are backwards for some reason
				value: field.dataType === DATE
			}),
			model: this.model.table
		}]
	},

	/**
	 * Creates a menu option using a BootslideMenu.MultipleChoiceOption
	 * contentView.
	 *
	 * @param data data used to construct the option
	 *
	 * @param [data.key] an i18n key used to choose a label for the option.  Can
	 * also be already-resolved text (e.g. a calendar name)
	 *
	 * @param [data.substitution] an optional substitution to use with data.key
	 * to choose a label for the option
	 *
	 * @param data.value the value for the menu option (e.g. day-of-the-week)
	 *
	 * @param [data.property='summaryType'] the property of the SettingsModel that
	 * should be modified if this option is chosen
	 */
	createMultiChoiceOption(data) {
		// If there is no i18n substitution, no need to pass it through the ArgumentTruncator
		var label = i18n.get(data.key)
		if (data.substitution) {
			label = new ArgumentTruncator({
				key: data.key,
				substitution: data.substitution
			}).render().el
		}
		return {
			label: i18n.get(data.key, data.substitution),
			contentView: BootslideMenu.MultipleChoiceOption.extend({
				label,
				value: data.value,
				property: data.property || 'summaryType'
			}),
			model: this.model
		}
	},

	/**
	 * Returns a target for a Bootslide menu item.  The target will trigger
	 * an event based on the provided arguments, where the first argument is
	 * the event name, and subsequent arguments are arguments to pass along with
	 * the triggered event
	 */
	eventTarget: function() {
		var args = arguments
		return function(e) {
			// don't allow Bootslide to change the URL
			e.preventDefault()
			this.trigger.apply(this, args)
		}.bind(this)
	}

})

var SettingsModel = Backbone.Model.extend(/** @lends {SettingsModel.prototype} */{
	initialize: function (attr, opt) {
		this.table = wrapModel(this.get('table') || {}, Backbone.Model)
		nestModel({
			parent: this,
			parentAttribute: 'table',
			child: this.table
		})
	}
})

/**
 * @param options the same options passed to datasets-result.js
 */
SettingsModel.fromOptions = function (options, dataset) {
	// note: don't deepClone the options so we can more easily modify things
	// (e.g. the filters)
	let summaryType = options.table.get('summaryType') ||
		getChooseSummaryTypeDefault(options.field, options.filters, dataset)

	let breakdownCalendarId = options.table.get('breakdownCalendarId')
	let summaryTypesWithoutBreakdowns = [ DAY_OF_THE_WEEK, MONTH_OF_THE_YEAR,
		HOUR_OF_THE_DAY, MINIMUM, MAXIMUM, UNIQUE, SIMILAR_TIME_RANGES,
		MIN_DATE, MAX_DATE, MIN_TIME, MAX_TIME ]
	if (!breakdownCalendarId && !_.includes(summaryTypesWithoutBreakdowns, summaryType)) {
		breakdownCalendarId = options.field?.defaultCalendarId
	}

	let groupCalendarId = options.table.get('groupCalendarId')
	if (!groupCalendarId && summaryType === 'similar-time-ranges') {
		groupCalendarId = options.field?.defaultCalendarId
	}

	// must be after the breakdownCalendarId and groupCalendarId are set
	if (_.includes([DAY_OF_THE_WEEK, MONTH_OF_THE_YEAR], summaryType)) {
		summaryType = SIMILAR_TIME_RANGES
	}

	const tableShowKeys = options.table.get('showKeys')
	let attributes = _.extend(cloneWithoutResponseDataset(options), {
		summaryType,
		dataset,
		setAsAppCalendar: dataset.get('selectedCalendarFieldId') == options.field?.id,
		abbreviateNumbers: _.get(options.table.attributes, 'abbreviateNumbers', true),
		showKeys: _.isNil(tableShowKeys) ? options.field?.showKeys : tableShowKeys,
		showAncestors: options.table.get('showAncestors'),
		breakdownCalendarId,
		groupCalendarId
	})
	return new SettingsModel(attributes)
}

TableSettings.aggregatedResult = aggregatedResult
TableSettings.SettingsModel = SettingsModel

export default TableSettings
TableSettings.__filename__ = 'datasets/overview/table-settings.js'
